import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { guestBenefits } from "@/data/attractions";
import {
  Bus,
  BookOpen,
  Bike,
  Target,
  Train,
  Mountain,
  ArrowUp,
  Key,
  Cable,
  Waves,
  Gamepad2,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/data/translations";

const GuestBenefits = () => {
  const { language } = useLanguage();

  // Map benefit IDs to translation keys
  const getBenefitTranslation = (benefitId: string) => {
    // Convert kebab-case IDs to camelCase for translation keys
    const camelCaseId = benefitId.replace(/-([a-z])/g, (match, letter) =>
      letter.toUpperCase(),
    );
    const translationKey = `localAttractions.guestBenefits.benefits.${camelCaseId}`;
    return {
      title: getTranslation(language, `${translationKey}.title`),
      description: getTranslation(language, `${translationKey}.description`),
    };
  };

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "Bus":
        return Bus;
      case "BookOpen":
        return BookOpen;
      case "Bike":
        return Bike;
      case "Target":
        return Target;
      case "Train":
        return Train;
      case "Mountain":
        return Mountain;
      case "ArrowUp":
        return ArrowUp;
      case "Key":
        return Key;
      case "Cable":
        return Cable;
      case "Waves":
        return Waves;
      case "Gamepad2":
        return Gamepad2;
      default:
        return Bus;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "free":
        return "bg-green-100 text-green-800 border-green-200";
      case "discount":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "priority":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "exclusive":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <section className="py-16 bg-pebble-beige" data-oid="65lz989">
      <div className="container mx-auto px-6" data-oid="qotf_w7">
        <div className="text-center mb-12" data-oid="jrmoh8-">
          <h2
            className="text-3xl md:text-4xl text-gray-900 font-playfair mb-4"
            data-oid="ks6ckqt"
          >
            {getTranslation(language, "localAttractions.guestBenefits.title")}
          </h2>
          <p
            className="text-lg text-gray-900 font-lato max-w-3xl mx-auto mb-8"
            data-oid="vgjfoxu"
          >
            {getTranslation(
              language,
              "localAttractions.guestBenefits.description",
            )}
          </p>

          <div
            className="flex flex-wrap justify-center gap-4 mb-8"
            data-oid="nwm328y"
          >
            <Badge
              className="bg-green-100 text-green-800 border border-green-200 px-3 py-1"
              data-oid="64hizab"
            >
              {getTranslation(
                language,
                "localAttractions.guestBenefits.freeBenefits",
              )}
            </Badge>
            <Badge
              className="bg-blue-100 text-blue-800 border border-blue-200 px-3 py-1"
              data-oid="1nhncha"
            >
              {getTranslation(
                language,
                "localAttractions.guestBenefits.discountBenefits",
              )}
            </Badge>
          </div>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          data-oid="0xyfy-v"
        >
          {guestBenefits.map((benefit) => {
            const IconComponent = getIcon(benefit.icon);
            const translatedBenefit = getBenefitTranslation(benefit.id);
            return (
              <Card
                key={benefit.id}
                className="text-center hover:shadow-lg transition-shadow duration-300 border-0 shadow-md bg-white"
                data-oid="b2z2zc7"
              >
                <CardContent className="pt-8 pb-6 px-6" data-oid="p.5gie4">
                  <div
                    className="w-16 h-16 mx-auto mb-4 bg-alpine-gold/10 rounded-full flex items-center justify-center"
                    data-oid="2wxglr0"
                  >
                    <IconComponent
                      className="w-8 h-8 text-alpine-gold"
                      data-oid="5delkkg"
                    />
                  </div>

                  <Badge
                    className={`mb-3 ${getCategoryColor(benefit.category)} border`}
                    data-oid=".c57qel"
                  >
                    {benefit.category.charAt(0).toUpperCase() +
                      benefit.category.slice(1)}
                  </Badge>

                  <h3
                    className="text-lg text-gray-900 font-playfair mb-3"
                    data-oid="qfop5ac"
                  >
                    {translatedBenefit.title}
                  </h3>

                  <p
                    className="text-sm text-gray-900 font-lato mb-4 leading-relaxed"
                    data-oid="110-0k4"
                  >
                    {translatedBenefit.description}
                  </p>

                  <div className="text-center" data-oid="y6:6nan">
                    <span
                      className="inline-block bg-alpine-gold text-white px-3 py-1 rounded-full text-sm font-semibold"
                      data-oid=":ku49y2"
                    >
                      {benefit.value}
                    </span>
                  </div>

                  {benefit.partner && (
                    <div
                      className="mt-3 text-xs text-gray-500"
                      data-oid=":j_fh1o"
                    >
                      {getTranslation(
                        language,
                        "localAttractions.guestBenefits.partner",
                      )}
                      : {benefit.partner}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-12" data-oid="0ze95iy">
          <div
            className="bg-white rounded-lg p-6 max-w-2xl mx-auto shadow-md"
            data-oid="7wlbpnx"
          >
            <h3
              className="text-xl text-gray-900 font-playfair mb-3"
              data-oid="968r120"
            >
              {getTranslation(
                language,
                "localAttractions.guestBenefits.howToGet",
              )}
            </h3>
            <p className="text-gray-600 font-lato mb-4" data-oid="ua8aj8p">
              {getTranslation(
                language,
                "localAttractions.guestBenefits.howToGetDesc",
              )}
            </p>
            <div className="text-sm text-gray-500" data-oid="xge5gso">
              {getTranslation(
                language,
                "localAttractions.guestBenefits.validDuringStay",
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GuestBenefits;
